/* eslint-disable pii/no-phone-number */
// https://github.com/inditex/app-zenit/blob/develop/apis/rest/zenit/zenit-api.yml

/* eslint-disable no-secrets/no-secrets */

import { Payload } from '../payload.model';
import { ZenitList, Section, ZenitEventName, ZenitPageType } from '../zenit-general.model';

import { BasicEvent } from './basic-event.model';

/**
 * Version of the PageHit event
 */
type Version = '2';

/**
 * Which part of the interface was the origin of this search results page
 * This property should be populated for pagehit events with pagetype=PRODUCT_SEARCH_RESULTS
 * Must be one of the enum list.
 */
export enum OriginPageHit {
    manual = 'manual',
    autocomplete = 'autocomplete',
    'top-queries' = 'top-queries',
    'related-tags' = 'related-tags',
    'previous-queries' = 'previous-queries',
    'next-queries' = 'next-queries',
    'no-results-fallback' = 'no-results-fallback',
    'alternative-queries' = 'alternative-queries',
}

/**
 * @property {PageType} pageType
 * @property {number} storeId
 * @property {number} [brandId]
 * @property {number} [categoryId]
 * @property {string | null} [categoryURL]
 * @property {number} [catentryId]
 * @property {string} [displayLanguage]
 * @property {List | null} [list]
 * @property {number | null} [listPosition]
 * @property {Origin | null} [origin]
 * @property {string} [pageMode]
 * @property {string} [partNumber]
 * @property {Section | null} [section]
 */
export interface PageHitContext {
    /** Type of page that user is accessing. Must be one of the list */
    pageType: ZenitPageType;
    storeId: number;
    brandId?: 11;
    categoryId?: number | null;
    categoryURL?: string | null;
    catentryId?: number;
    displayLanguage?: string;
    /** Metadata of the different active experimentation providers present along users navigation */
    experiments: ZenitExperiments;
    /**
    * Which list was the origin of a selected search result
    * This property should be populated when reaching a PRODUCT_DETAILS page from a search list
    * Must be one of the enum list.
    */
    list?: ZenitList | null;
    listPosition?: number | null;
    origin?: OriginPageHit | null;
    pageMode?: string;
    /** The Partnumber uniquely identifies any element in the catalog. Must have the format as in the next example: "C01165402712000-V2023". NOTE: If value cannot be sent use a code value of -1' */
    partnumber: string;
    /** Section that user is accessing. Must be one of the enum list */
    section?: Section | null;
}

/**
 * @extends BasicEvent
 *
 * @property {EventName} eventName
 * @property {Payload} payload
 * @property {Version} version
 */
export interface PageHitEvent extends BasicEvent {
    /** Type of the event. Must be PageHit */
    eventName: ZenitEventName.PageHit;
    payload: Payload<PageHitContext>;
    version: Version;
}

// #region EXPERIMENTS

export interface ZenitExperiments {
    /** Array with each of the active experiments that the users navigates through during a session */
    optimizely: ZenitExperiment[];
}

export interface ZenitExperiment {
    /** Additional details of the Optimizely experiment */
    experimentDetails?: ZenitExperimentDetails;
    /** Name of the Optimizely experiment */
    experimentName: string;
    /** Key of the Optimizely experiment */
    experimentKey: string;
    /** Variation of the Optimizely experiment */
    variationKey: string;
    /** Name of the provider of the experiment */
    provider: string;
}
export interface ZenitExperimentDetails {
    /** Account ID associated to the Optimizely experiment */
    experimentAccountId: number;
    /** Project ID associated to the Optimizely experiment */
    experimentProjectId: number;
    /** Campaign ID associated to the Optimizely experiment */
    experimentCampaignId: number;
    /** Experiment ID associated to the Optimizely experiment */
    experimentId: number;
}

// #endregion
